import { useEffect, useState } from "react";
import { getStaffReportDraft, listStaffReportDrafts } from "@/api/staff_reports";
import { ChevronRight, FileText } from "lucide-react";
import { StaffReportStageBadge } from "@/components/staff_reports/StaffReportStageBadge";
import { Button } from "@/components/ui/button";
import { PaginatedTableControls } from "@/components/ui/paginated-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useMunicipality } from "@/context/MunicipalityContext";
import { useStaffReports } from "@/context/StaffReportsContext";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { emptyPagination } from "@/lib/jsonPagination";

export function StaffReportDraftsList({ activeDraftId }: { activeDraftId?: number | null }) {
  const { municipality } = useMunicipality();
  const {
    selectedDraft,
    setSelectedDraft,
    drafts,
    setDrafts,
    nextDraftSelectionVersion,
    getDraftSelectionVersion,
    beforeLeaveReportRef,
  } = useStaffReports();
  const [listError, setListError] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  const [pageRequest, setPageRequest] = useState({ municipalityId: municipality?.id, page: 1 });
  const [pagination, setPagination] = useState(() => emptyPagination(50));
  const page = pageRequest.municipalityId === municipality?.id ? pageRequest.page : 1;
  const error = selectionError ?? listError;

  useEffect(() => {
    let cancelled = false;
    let fetching = false;
    const run = async () => {
      if (fetching || document.visibilityState === "hidden") return;
      if (!municipality?.id) {
        setDrafts([]);
        return;
      }
      fetching = true;
      try {
        const list = await listStaffReportDrafts({ municipalityId: municipality.id, page });
        if (!cancelled) {
          setDrafts(list.drafts);
          setPagination(list.pagination);
          setListError(null);
        }
      } catch (e) {
        if (!cancelled) setListError(e instanceof Error ? e.message : "Could not load reports.");
      } finally {
        fetching = false;
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    setDrafts([]);
    setListError(null);
    setSelectionError(null);
    void run();
    const interval = window.setInterval(run, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [municipality?.id, setDrafts, retry, page]);

  if (!municipality?.id) return null;

  const activeDrafts = drafts.filter((d) => !d.discarded_at);

  return (
    <section aria-label="Report history" className="w-full space-y-3">
      {error && (
        <p className="text-xs text-destructive mb-1" role="alert">
          {error}
        </p>
      )}
      {error && (
        <Button variant="outline" size="sm" onClick={() => setRetry((value) => value + 1)}>
          Reload reports
        </Button>
      )}
      {loading && (
        <p className="text-sm text-muted-foreground" role="status">
          Loading reports…
        </p>
      )}
      <div className="overflow-hidden rounded-lg border bg-card">
        <Table aria-label="Reports" className="table-fixed">
          <colgroup>
            <col />
            <col className="w-40 sm:w-44" />
            <col className="hidden w-28 sm:table-column" />
            <col className="w-10" />
          </colgroup>
          <TableHeader className="sr-only">
            <TableRow className="hover:bg-transparent">
              <TableHead scope="col" className="px-4">
                Report
              </TableHead>
              <TableHead scope="col" className="w-40 px-3 sm:w-44">
                Stage
              </TableHead>
              <TableHead scope="col" className="hidden w-28 px-3 sm:table-cell">
                Updated
              </TableHead>
              <TableHead scope="col" className="w-10">
                <span className="sr-only">Open report</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeDrafts.map((d) => (
              <TableRow
                key={d.id}
                className="group cursor-pointer focus-within:bg-muted/50"
                data-state={(activeDraftId ?? selectedDraft?.id) === d.id ? "selected" : undefined}
                onClick={async () => {
                  if (beforeLeaveReportRef?.current && !(await beforeLeaveReportRef.current()))
                    return;
                  const requestVersion = nextDraftSelectionVersion();
                  setSelectionError(null);
                  try {
                    const full = await getStaffReportDraft(d.id);
                    if (getDraftSelectionVersion() !== requestVersion) return;
                    if (full.municipality_id !== municipality.id) return;
                    setSelectedDraft(full);
                  } catch (e) {
                    if (getDraftSelectionVersion() !== requestVersion) return;
                    const status =
                      typeof e === "object" && e !== null && "status" in e
                        ? Number((e as { status: unknown }).status)
                        : undefined;
                    if (status === 404) {
                      setDrafts((prev) =>
                        prev.map((item) =>
                          item.id === d.id
                            ? { ...item, discarded_at: new Date().toISOString() }
                            : item,
                        ),
                      );
                      if (selectedDraft?.id === d.id) setSelectedDraft(null);
                    }
                    if (e instanceof Error && e.message) setSelectionError(e.message);
                  }
                }}
              >
                <TableCell className="px-4 py-2">
                  <Button
                    variant="ghost"
                    className="h-auto w-full justify-start gap-3 whitespace-normal px-0 py-2 text-left hover:bg-transparent"
                    aria-current={
                      (activeDraftId ?? selectedDraft?.id) === d.id ? "page" : undefined
                    }
                    aria-label={d.name}
                    title={d.name}
                  >
                    <FileText
                      className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block"
                      aria-hidden="true"
                    />
                    <span className="line-clamp-2 break-words font-medium">{d.name}</span>
                  </Button>
                </TableCell>
                <TableCell className="px-3 py-4">
                  {d.stage && <StaffReportStageBadge stage={d.stage} />}
                </TableCell>
                <TableCell className="hidden px-3 py-4 text-xs text-muted-foreground sm:table-cell">
                  {formatRelativeTime(d.updated_at)}
                </TableCell>
                <TableCell className="pr-4 text-muted-foreground group-hover:text-foreground group-focus-within:text-foreground">
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </TableCell>
              </TableRow>
            ))}
            {!loading && !error && activeDrafts.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="p-6 text-center text-muted-foreground">
                  No reports yet. Start a new report.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {pagination.total_pages > 1 && (
        <PaginatedTableControls
          variant="compact"
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          perPage={pagination.per_page}
          totalCount={pagination.total_count}
          onPageChange={(nextPage) => setPageRequest({ municipalityId: municipality.id, page: nextPage })}
        />
      )}
    </section>
  );
}
